import { DonationMetadata, JourneyContext, Member } from "@flow/common/types";
import { EmailInput } from "@flow/frontend/member/components/EmailInput";
import { PostcodeInput } from "@flow/frontend/member/components/PostcodeInput";
import { RequiredTextArea } from "@flow/frontend/member/components/RequiredTextArea";
import { error } from "@flow/lib/error";
import {
  Box,
  Button,
  CircularProgress,
  InputBaseComponentProps,
  Paper,
  Stack,
  TextField,
  TextFieldProps,
  Typography,
} from "@mui/material";
import {
  CardCvcElement,
  CardElement,
  CardExpiryElement,
  CardNumberElement,
  Elements,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import * as stripeJs from "@stripe/stripe-js";
import * as React from "react";
import * as Safe from "safe-portals";
import * as routes from "../../common/routes";
import { memberTheme } from "@flow/frontend/member/memberTheme";

// Use stripe's ultra-compact card entry widget?
const SHORT_STRIPE_FORM = false;

interface StripeCheckoutProps {
  ctx: JourneyContext;
  action_guid: string;
  stripePromise: PromiseLike<stripeJs.Stripe | null> | null;
  variant_idx: number;
  amount: number;
  metadata: DonationMetadata;
  hide_donation: boolean;
  isRegular: boolean;
  onClickBack: () => void;
  onPaymentSuccess: (donation_attempt_guid: string, member: Member) => void;
  form: React.MutableRefObject<HTMLFormElement | null>;
  redacted_prefill?: { name?: string; has_email: boolean; has_postcode: boolean };
  useMemberPrefill: boolean;
  setUseMemberPrefill: React.Dispatch<React.SetStateAction<boolean>>;
}

export const StripeCheckout = (props: StripeCheckoutProps) => {
  // the 'amount' value passed in options is required in lowest denomination of the currency (e.g. pence for GBP)
  return (
    <Elements
      stripe={props.stripePromise}
      options={{ mode: "payment", currency: "gbp", amount: Math.round(props.amount * 100) }}
    >
      <StripeCheckoutInternal {...props} />
    </Elements>
  );
};

const StripeCheckoutInternal = (props: StripeCheckoutProps) => {
  const [member, setMember] = React.useState(props.ctx.member);
  const [processing, setProcessing] = React.useState(true);
  const [errorMsg, setErrorMsg] = React.useState<string | undefined>(undefined);
  const stripe = useStripe();
  const elements = useElements();

  React.useEffect(() => {
    // is stripePromise is not null then set processing to false
    // else useEffect handler will be called again when it becomes not null.
    props.stripePromise?.then(() => setProcessing(false));
  }, [props.stripePromise]);

  function rejectPrefill(e: React.MouseEvent) {
    e.preventDefault();
    props.setUseMemberPrefill(false);
  }

  const handleClickDonate = async (e: React.FormEvent) => {
    e.preventDefault();
    setProcessing(true);

    const { name, email, postcode } = member;

    if (stripe === null || elements === null) {
      setProcessing(false);
      setErrorMsg("Something has gone wrong, please reload the page and try again");
      return;
    }

    if (props.form.current) {
      if (!props.form.current.checkValidity()) {
        setProcessing(false);
        setErrorMsg("Please check your details and try again");
        return;
      }
    } else {
      setProcessing(false);
      setErrorMsg("Something has gone wrong, please reload the page and try again");
      return;
    }

    if (!name || !email || (!SHORT_STRIPE_FORM && !postcode)) {
      setProcessing(false);
      return;
    }

    // XXX this code path should never happen (handleClickDonate should depend on these being valid already)
    if (!props.useMemberPrefill && (!name || !email)) {
      setProcessing(false);
      setErrorMsg("Something has gone wrong, please reload the page and try again");
      return error("Missing member name and/or email");
    }

    setErrorMsg(undefined);

    let cardElement: stripeJs.StripeCardElement | stripeJs.StripeCardNumberElement | null;
    cardElement = elements.getElement(CardNumberElement);
    if (SHORT_STRIPE_FORM) {
      cardElement = elements.getElement(CardElement);
    }
    if (cardElement === null) {
      setProcessing(false);
      setErrorMsg("Something has gone wrong, please reload the page and try again");
      return;
    }
    // TODO: this style of Stripe payment flow is outdated,
    //      modern flows use the fact that createPaymentMethod is implicit in comfirmPayment/confirmCardPayment ops
    //      it remains here because Flow backend API (routes.createStripePaymentIntent) expects a payment_method_id
    //      this API would need reworking to accommodate an modernised payment flow.
    const pm = await stripe.createPaymentMethod({
      type: "card",
      card: cardElement,
      billing_details: { name, email, address: { postal_code: postcode } },
    });

    if (pm.error) {
      setErrorMsg(pm.error.message);
      setProcessing(false);
      return;
    }

    const paymentMethod = Safe.obj({ id: Safe.str }).read(pm.paymentMethod);

    // Call backend to create payment intent
    const intent_result = await routes.createStripePaymentIntent.call({
      action_guid: props.action_guid,
      variant_idx: props.variant_idx,
      payment_method_id: paymentMethod.id,
      journey_context: { ...props.ctx, member },
      amount: props.amount,
      metadata: props.metadata,
      hide_donation: props.hide_donation,
      is_subscription: props.isRegular,
      use_prefill_member_data: props.useMemberPrefill,
    });

    if (Safe.Result.isErr(intent_result)) {
      setErrorMsg(intent_result.error);
      setProcessing(false);
      return;
    }

    const intent = intent_result.ok;

    // Finalize as necessary (can need additional card confirmation stage)
    if (props.isRegular) {
      if (intent.status === "requires_action" || intent.status === "requires_payment_method") {
        await stripe.confirmCardPayment(intent.client_secret);
      }
      completePayment(intent.donation_attempt_guid);
    } else {
      await stripe.confirmPayment({
        clientSecret: intent.client_secret,
        redirect: "if_required",
        confirmParams: {
          payment_method_data: {
            billing_details: { name },
          },
        },
      });
      completePayment(intent.donation_attempt_guid);
    }
    return;
  };

  function completePayment(donation_attempt_guid: string) {
    props.onPaymentSuccess(donation_attempt_guid, member);
  }

  return (
    <Box sx={{ position: "relative" }}>
      {processing && (
        <Box
          sx={{
            height: "100%",
            position: "absolute",
            width: "100%",
            backgroundColor: memberTheme.palette.greys.grey100,
            opacity: "0.7",
            zIndex: 100,
          }}
        >
          <CircularProgress sx={{ margin: "40%" }} />
        </Box>
      )}
      <Stack spacing={3}>
        {props.redacted_prefill && props.useMemberPrefill ? (
          <Paper sx={{ p: 2, marginBottom: "1rem" }}>
            You&apos;re donating as <strong className="posthog-mask-text">{props.redacted_prefill.name}</strong>
            <Button variant="text" onClick={rejectPrefill}>
              Not you? Click here
            </Button>
          </Paper>
        ) : (
          <>
            <RequiredTextArea
              value={member.name || ""}
              label="Name"
              id="member-name"
              onChange={(name) => setMember({ ...member, name })}
              sx={{ marginBottom: "0" }}
              inputLabelShrink={true}
            />
            <EmailInput
              value={member.email || ""}
              onChange={(email) => setMember({ ...member, email })}
              inputLabelShrink={true}
            />
            {!SHORT_STRIPE_FORM && (
              <PostcodeInput
                value={member.postcode || ""}
                onChange={(postcode) => setMember({ ...member, postcode })}
                inputLabelShrink={true}
              />
            )}
          </>
        )}

        {SHORT_STRIPE_FORM ? <CardElement /> : <CardDetailsForm />}
        {errorMsg && <Typography color="alert">{errorMsg}</Typography>}

        {/* TODO fullwidth buttons on mobile and changeAmount button shoudl be grey */}
        <Stack direction="column" spacing={2}>
          <Button
            fullWidth
            variant="contained"
            color="secondary"
            size="medium"
            disableElevation
            onClick={props.onClickBack}
            sx={{
              backgroundColor: memberTheme.palette.greys.grey300,
              color: memberTheme.palette.primary.dark,
            }}
          >
            Change amount
          </Button>
          <Button fullWidth id="donate-stripe-finalize" onClick={handleClickDonate} disabled={!stripe}>
            Donate £{props.amount} {props.isRegular ? "/ month" : ""}
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};

/**
 *
 * The below code integrates Stripe card elements with MUI TextField elements.
 * It is taken from https://github.com/mui/material-ui/issues/16037#issuecomment-498664873
 * as recommended by MUI here, https://mui.com/material-ui/react-text-field/#integration-with-3rd-party-input-libraries
 *
 */

function CardDetailsForm() {
  const [state, setState] = React.useState({
    cardNumberComplete: false,
    expiredComplete: false,
    cvcComplete: false,
    cardNumberError: undefined,
    expiredError: undefined,
    cvcError: undefined,
  });

  const onElementChange =
    (field: string, errorField: string) =>
    (
      event:
        | stripeJs.StripeCardNumberElementChangeEvent
        | stripeJs.StripeCardExpiryElementChangeEvent
        | stripeJs.StripeCardCvcElementChangeEvent
    ) => {
      setState({ ...state, [field]: event.complete, [errorField]: event.error?.message });
    };

  const { cardNumberError, expiredError, cvcError } = state;

  return (
    <Stack spacing={3}>
      <StripeTextFieldNumber
        error={Boolean(cardNumberError)}
        labelErrorMessage={cardNumberError}
        onChange={onElementChange("cardNumberComplete", "cardNumberError")}
      />
      <StripeTextFieldExpiry
        error={Boolean(expiredError)}
        labelErrorMessage={expiredError}
        onChange={onElementChange("expiredComplete", "expiredError")}
      />
      <StripeTextFieldCVC
        error={Boolean(cvcError)}
        labelErrorMessage={cvcError}
        onChange={onElementChange("cvcComplete", "cvcError")}
      />
    </Stack>
  );
}

const StripeInput = React.forwardRef<any, InputBaseComponentProps>(function StripeInput(props, ref) {
  const { component: Component, options, ...other } = props;
  const [mountNode, setMountNode] = React.useState<any | null>(null);

  React.useImperativeHandle(
    ref,
    () => ({
      focus: () => mountNode.focus(),
    }),
    [mountNode]
  );

  return (
    <Component
      onReady={setMountNode}
      options={{
        ...options,
      }}
      {...other}
    />
  );
});

type StripeElement = typeof CardCvcElement | typeof CardExpiryElement | typeof CardNumberElement;

interface StripeTextFieldProps<T extends StripeElement>
  extends Omit<TextFieldProps, "onChange" | "inputComponent" | "inputProps"> {
  inputProps?: React.ComponentProps<T>;
  labelErrorMessage?: string;
  onChange?: React.ComponentProps<T>["onChange"];
  stripeElement?: T;
}

export const StripeTextField = <T extends StripeElement>(props: StripeTextFieldProps<T>) => {
  const {
    helperText,
    InputLabelProps,
    InputProps = {},
    inputProps,
    error,
    labelErrorMessage,
    stripeElement,
    ...other
  } = props;

  return (
    <TextField
      fullWidth
      InputLabelProps={{
        ...InputLabelProps,
        shrink: true,
      }}
      error={error}
      InputProps={{
        ...InputProps,
        inputProps: {
          ...inputProps,
          ...InputProps.inputProps,
          component: stripeElement,
        },
        inputComponent: StripeInput,
      }}
      helperText={error ? labelErrorMessage : helperText}
      {...(other as any)}
    />
  );
};

function StripeTextFieldNumber(props: StripeTextFieldProps<typeof CardNumberElement>) {
  return <StripeTextField label="Credit Card Number" stripeElement={CardNumberElement} {...props} />;
}

function StripeTextFieldExpiry(props: StripeTextFieldProps<typeof CardExpiryElement>) {
  return <StripeTextField label="Expires" stripeElement={CardExpiryElement} {...props} />;
}

function StripeTextFieldCVC(props: StripeTextFieldProps<typeof CardCvcElement>) {
  return <StripeTextField label="CVC Code" stripeElement={CardCvcElement} {...props} />;
}
